<script setup lang="ts">
import { useI18n } from '@/i18n';
import { createLostPasswordSchema, type LostPasswordFormValues } from '@/schemas/lostPassword';
import { useUserStore } from '@/stores/user';
import { Alert, Button, Form, Heading, Input, LayoutGap } from '@dabble/quillui';
import AuthLayout from '@/components/AuthLayout/AuthLayout.vue';
import { toTypedSchema } from '@vee-validate/yup';
import { useForm, useIsFormDirty } from 'vee-validate';
import { computed, ref } from 'vue';

const { t, te } = useI18n();

function translateFieldError(raw: string | undefined) {
  if (!raw) return '';
  return te(raw) ? t(raw) : raw;
}

const userStore = useUserStore();

const error = ref<string | undefined>(undefined);
const showSuccess = ref(false);

const rawSchema = computed(() => createLostPasswordSchema((key: string) => t(key)));
const validationSchema = computed(() => toTypedSchema(rawSchema.value));

const { handleSubmit, defineField, values } = useForm<LostPasswordFormValues>({
  validationSchema,
  initialValues: {
    email: '',
  },
});

const isValid = computed(() =>
  rawSchema.value.isValidSync({
    email: values.email ?? '',
  }),
);

const isDirty = useIsFormDirty();

const fieldOpts = {
  validateOnBlur: true,
  validateOnChange: true,
  validateOnModelUpdate: true,
  props: (state: { errors: string[] }) => ({
    error: state.errors.length > 0,
    errorMessage: translateFieldError(state.errors[0]),
  }),
};

const [email, emailField] = defineField('email', fieldOpts);

const errorMessage = computed(() => {
  const raw = error.value;
  if (!raw) return '';
  return te(raw) ? t(raw) : raw;
});

const onSubmit = handleSubmit(async (values: LostPasswordFormValues) => {
  error.value = undefined;
  showSuccess.value = false;
  try {
    await userStore.requestPasswordReset(values.email);
    showSuccess.value = true;
  } catch (e: unknown) {
    const message = e instanceof Error ? e.message : String(e);
    error.value = te(message) ? t(message) : message;
  }
});
</script>

<template>
  <AuthLayout>
    <Form v-model:dirty="isDirty" :valid="isValid" :actions="false" @submit="onSubmit">
      <LayoutGap direction="column" size="small" align="center" class="lost-password__form-contents">
        <Heading :level="3" :margin="false">{{ t('lost_password_title') }}</Heading>
        <div role="alert" aria-live="assertive" aria-atomic="true">
          <Alert v-if="errorMessage" variant="warning">{{ errorMessage }}</Alert>
          <Alert v-if="showSuccess" variant="success">{{ t('lost_password_success') }}</Alert>
        </div>
        <LayoutGap direction="column" align="center" size="large" class="lost-password__form-stack">
          <Input
            id="email"
            v-model="email"
            class="auth-field"
            v-bind="emailField"
            :aria-label="t('email')"
            type="text"
            required
            size="large"
            autocapitalize="off"
            :disabled="userStore.isLoading"
            :placeholder="t('signup_placeholder_email')"
          />
          <Button class="auth-submit" type="submit" :disabled="userStore.isLoading || !isValid" fill-width size="large">
            {{ userStore.isLoading ? t('lost_password_sending') : t('lost_password_send') }}
          </Button>
        </LayoutGap>
      </LayoutGap>
    </Form>

    <template #footer>
      <p class="lost-password__login-link">
        {{ t('lost_password_remember') }}
        <router-link to="/auth/login">{{ t('lost_password_login') }}</router-link>
      </p>
    </template>
  </AuthLayout>
</template>

<style lang="scss" scoped>
@use '@/styles/auth-form.scss';

.lost-password {
  &__login-link {
    margin: 0;
  }
}
</style>
